import React from "react";
import { SlideAction } from "./SlideAction";

import FormatItalicIcon from "@material-ui/icons/FormatItalic";
import FormatBoldIcon from "@material-ui/icons/FormatBold";
import FormatUnderlinedIcon from "@material-ui/icons/FormatUnderlined";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import FormatListNumberedIcon from "@material-ui/icons/FormatListNumbered";
import FormatQuoteIcon from "@material-ui/icons/FormatQuote";
import { useTextToSpeech } from "./texttospeech/useTextToSpeech";
import { useOfflineStatus } from "../../../utilities/useOfflineStatus";
import { Button, makeStyles, Typography } from "@material-ui/core";

const useStyles = makeStyles(() => ({
	slidePrompt: {
		position: "absolute",
		zIndex: 9999,
		backgroundColor: "#000",
		borderRadius: 7,
		color: "white",
	},
	desktopBallonEditorButton: {
		margin: "3px 2px",
		padding: "6px 10px",
		minWidth: 40,
		borderRadius: 3,
		backgroundColor: "transparent",
		height: 36,
		opacity: 1,
		transition: "all 0.3s ease 0s",
		"&:hover": {
			transition: "all 0.3s ease 0s",
			backgroundColor: "#ffffff",
			color: "#000",
		},
		color: "white",
	},
	slidePromptArrowContainer: {
		position: "absolute",
	},
	slidePromptArrowInverted: {
		top: "-50px",
		left: 0,
		width: 0,
		position: "absolute",
		borderBottom: "8px solid #000",
		borderLeft: "10px solid transparent",
		borderRight: "10px solid transparent",
	},
	slidePromptArrow: {
		borderTop: "8px solid #000",
		borderLeft: "10px solid transparent",
		borderRight: "10px solid transparent",
		left: 0,
		position: "absolute",
		width: 0,
	},
	verticalDivider: {
		paddingBottom: 12,
		paddingTop: 10,
		borderRight: "1px solid white",
	},
	isActive: {
		transition: "all 0.3s ease 0s",
		backgroundColor: "#ffffff",
		color: "#000",
	},
	textToSpeechButton: {
		margin: "3px 2px",
		padding: "6px 10px",
		minWidth: 40,
		borderRadius: 3,
		backgroundColor: "transparent",
		height: 36,
		opacity: 1,
		transition: "all 0.3s ease 0s",
		"&:hover": {
			transition: "all 0.3s ease 0s",
			backgroundColor: "#ffffff",
			color: "#000",
		},
		color: "white",
		"&:disabled": {
			color: "grey",
		},
	},
}));

export type DesktopFormatBubbleSettings = {
	left: number;
	top: number;
	width: number;
	message: string;
	action: SlideAction;
	isBolded: boolean;
	isItalic: boolean;
	isUnderlined: boolean;
	invertArrowDirection: boolean;
};

interface Props {
	settings: DesktopFormatBubbleSettings;
	onClickSlidePrompt: (slideAction: SlideAction) => void;
	excecuteEditorAction: (action: string, value?: string) => void;
}

export function DesktopFormatBubble(props: Props) {
	const desktopSlidePrompt = props.settings;
	const classes = useStyles();
	const { startTextToSpeech } = useTextToSpeech();
	const isOnline = !useOfflineStatus();

	return (
		<div
			className={classes.slidePrompt}
			style={{
				left: desktopSlidePrompt.left,
				top: desktopSlidePrompt.top,
			}}
		>
			<Button className={classes.desktopBallonEditorButton} onClick={() => props.onClickSlidePrompt(desktopSlidePrompt.action)}>
				<Typography color="inherit">{desktopSlidePrompt.message}</Typography>
			</Button>
			<div className={classes.slidePromptArrowContainer} style={{ left: desktopSlidePrompt.width / 2 - 10 }}>
				<div className={desktopSlidePrompt.invertArrowDirection ? classes.slidePromptArrowInverted : classes.slidePromptArrow} />
			</div>
			<span className={classes.verticalDivider} />
			{isOnline && (
				<Button className={classes.textToSpeechButton} onClick={() => startTextToSpeech()}>
					Hear It
				</Button>
			)}
			<span className={classes.verticalDivider} />
			<Button className={classes.desktopBallonEditorButton} onClick={() => props.excecuteEditorAction("heading", "heading1")}>
				H1
			</Button>
			<Button className={classes.desktopBallonEditorButton} onClick={() => props.excecuteEditorAction("heading", "heading2")}>
				H2
			</Button>
			<Button className={classes.desktopBallonEditorButton} onClick={() => props.excecuteEditorAction("heading", "paragraph")}>
				P
			</Button>
			<span className={classes.verticalDivider} />
			<Button
				className={
					desktopSlidePrompt.isBolded ? classes.desktopBallonEditorButton + " " + classes.isActive : classes.desktopBallonEditorButton
				}
				onClick={() => props.excecuteEditorAction("bold")}
			>
				<FormatBoldIcon fontSize="small" />
			</Button>
			<Button
				className={
					desktopSlidePrompt.isItalic ? classes.desktopBallonEditorButton + " " + classes.isActive : classes.desktopBallonEditorButton
				}
				onClick={() => props.excecuteEditorAction("italic")}
			>
				<FormatItalicIcon fontSize="small" />
			</Button>
			<Button
				className={
					desktopSlidePrompt.isUnderlined ? classes.desktopBallonEditorButton + " " + classes.isActive : classes.desktopBallonEditorButton
				}
				onClick={() => props.excecuteEditorAction("underline")}
			>
				<FormatUnderlinedIcon fontSize="small" />
			</Button>
			<span className={classes.verticalDivider} />
			<Button className={classes.desktopBallonEditorButton} onClick={() => props.excecuteEditorAction("bulletedList")}>
				<FormatListBulletedIcon fontSize="small" />
			</Button>
			<Button className={classes.desktopBallonEditorButton} onClick={() => props.excecuteEditorAction("numberedList")}>
				<FormatListNumberedIcon fontSize="small" />
			</Button>
			<Button className={classes.desktopBallonEditorButton} onClick={() => props.excecuteEditorAction("blockQuote")}>
				<FormatQuoteIcon fontSize="small" />
			</Button>
		</div>
	);
}